/**
 * Deeper spells on the HUD (docs/spells-depth.md): the two stash pods by the
 * queue, the weave pill ("C: Weave → Obsidian", or "→ ???" for a recipe not
 * found yet), the weave's progress over the queue, and the Grand Magick pill
 * (ready, incanting, or cooling down). Layout comes from hudLayout.ts.
 */

import type { Element } from '@shared/index.js';
import { GRAND_MAGICKS, elementName, tierOf } from '@shared/index.js';
import type { LocalView } from '../state/viewModel.js';
import { ELEMENT_SWATCH, UI, elementColor, mixHex, rgba } from '../palette.js';
import { font, roundRectPath, type Ctx } from './gfx.js';
import { drawElementIcon, drawElementGlyph } from './glyphs.js';
import { GAP, SLOT, queueTop, stashPodsLayout, weavePillLayout, magickPillLayout, PILL_H } from './hudLayout.js';

/** Key labels for the pods and the weave pill; Controls sets them from the keymap. */
let labels = { weave: 'C', stash: ['Z', 'X'] as [string, string], magick: 'Space' };

export function setDepthKeyLabels(next: { weave: string; stash: [string, string]; magick: string }): void {
  labels = next;
}

/** Draw one piece in a tile: its icon, or until the icons land its glyph (woven pieces: their short label). */
export function drawPiece(ctx: Ctx, el: Element, x: number, y: number, size: number): void {
  ctx.fillStyle = ELEMENT_SWATCH[el].dark;
  if (!drawElementIcon(ctx, el, x, y, size * 0.62)) drawElementGlyph(ctx, el, x, y, size * 0.52);
}

export class DepthHud {
  private podFlash: [number, number] = [0, 0];

  /** A stash tap landed: the pod flashes. */
  flashPod(slot: number): void {
    if (slot === 0 || slot === 1) this.podFlash[slot] = 1;
  }

  step(dt: number): void {
    this.podFlash = [Math.max(0, this.podFlash[0] - dt * 3), Math.max(0, this.podFlash[1] - dt * 3)];
  }

  draw(ctx: Ctx, me: LocalView, w: number, h: number, s: number, time: number, touch: boolean, knows: (el: Element) => boolean, magickPillShown: boolean): void {
    this.drawPods(ctx, me, w, h, s, time, touch);
    this.drawWeaving(ctx, me, w, h, s, time);
    if (me.grand) this.drawGrand(ctx, me, w, h, s, time);
    else if (me.weaveNext && !me.weaving) this.drawWeavePill(ctx, me, w, h, s, touch, knows, magickPillShown);
  }

  private drawPods(ctx: Ctx, me: LocalView, w: number, h: number, s: number, time: number, touch: boolean): void {
    const pods = stashPodsLayout(w, h, s);
    const stash = me.stash ?? [null, null];
    const used = me.grandSlots ?? [];
    for (let i = 0; i < pods.length; i++) {
      const p = pods[i]!;
      const el = stash[i] ?? null;
      const flash = this.podFlash[i as 0 | 1];
      const cx = p.x + p.size / 2, cy = p.y + p.size / 2;
      roundRectPath(ctx, p.x, p.y, p.size, p.size, 7 * s);
      if (el) {
        ctx.fillStyle = mixHex(UI.panelSolid, mixHex(elementColor(el), UI.white, flash * 0.6), 0.35 + flash * 0.3);
        ctx.fill();
        const ready = used.includes(i);
        ctx.strokeStyle = ready ? rgba(UI.gold, 0.7 + 0.3 * Math.sin(time * 6)) : rgba(elementColor(el), 0.9);
        ctx.lineWidth = ready ? 2.5 : 1.5;
        ctx.stroke();
        drawPiece(ctx, el, cx, cy, p.size);
        // tier pips, top right: one per tier above the first
        const pips = tierOf(el) - 1;
        for (let k = 0; k < pips; k++) {
          ctx.fillStyle = UI.gold;
          ctx.beginPath();
          ctx.arc(p.x + p.size - (4 + k * 5) * s, p.y + 4 * s, 1.8 * s, 0, Math.PI * 2);
          ctx.fill();
        }
      } else {
        ctx.fillStyle = rgba(UI.panelSolid, 0.55 + flash * 0.3);
        ctx.fill();
        ctx.setLineDash([3 * s, 3 * s]);
        ctx.strokeStyle = rgba(UI.white, 0.25 + flash * 0.5);
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.setLineDash([]);
      }
      if (!touch) {
        ctx.font = font(9 * s, 800);
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillStyle = UI.muted;
        ctx.fillText(labels.stash[i] ?? '', cx, p.y - 11 * s);
      }
    }
  }

  /** The pill's frame: a dark capsule with a coloured edge. */
  private pill(ctx: Ctx, x: number, y: number, pw: number, ph: number, edge: string, s: number): void {
    roundRectPath(ctx, x, y, pw, ph, ph / 2);
    ctx.fillStyle = UI.panel;
    ctx.fill();
    ctx.strokeStyle = edge;
    ctx.lineWidth = 1.5 * s;
    ctx.stroke();
  }

  private drawWeavePill(ctx: Ctx, me: LocalView, w: number, h: number, s: number, touch: boolean, knows: (el: Element) => boolean, stacked: boolean): void {
    const r = me.weaveNext;
    if (!r) return;
    const known = knows(r.out);
    // On a phone the cap already says Weave, so the pill only names the result.
    const key = touch ? 'Weave' : labels.weave;
    const label = `${touch ? '' : 'Weave  '}→  ${known ? elementName(r.out) : '???'}`;
    ctx.font = font(15 * s, 800);
    const textW = ctx.measureText(label).width;
    ctx.font = font(12 * s, 800);
    const keyW = ctx.measureText(key).width + 12 * s;
    const pw = 12 * s + keyW + 8 * s + textW + 14 * s + (known ? 22 * s : 0);
    const L = weavePillLayout(w, h, s, pw, stacked);
    const edge = known ? elementColor(r.out) : UI.muted;
    this.pill(ctx, L.x, L.y, L.w, L.h, edge, s);
    let x = L.x + 12 * s;
    const cy = L.y + L.h / 2;
    // key cap
    roundRectPath(ctx, x, cy - 9 * s, keyW, 18 * s, 4 * s);
    ctx.fillStyle = rgba(UI.white, 0.12);
    ctx.fill();
    ctx.fillStyle = UI.text;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(key, x + keyW / 2, cy);
    x += keyW + 8 * s;
    ctx.font = font(15 * s, 800);
    ctx.textAlign = 'left';
    ctx.fillStyle = known ? UI.text : UI.muted;
    ctx.fillText(label, x, cy);
    if (known) drawPiece(ctx, r.out, L.x + L.w - 20 * s, cy, 22 * s);
  }

  /** A weave in progress: a gold thread filling over the last two slots, and what it will be. */
  private drawWeaving(ctx: Ctx, me: LocalView, w: number, h: number, s: number, time: number): void {
    const wv = me.weaving;
    if (!wv) return;
    const n = Math.max(2, me.queue.length);
    const barW = (SLOT * 5 + GAP * 4) * s;
    const x0 = w / 2 - barW / 2 + (n - 2) * (SLOT + GAP) * s;
    const ww = (SLOT * 2 + GAP) * s;
    const y = queueTop(h, s) - 7 * s;
    roundRectPath(ctx, x0, y, ww, 4 * s, 2 * s);
    ctx.fillStyle = rgba(UI.white, 0.15);
    ctx.fill();
    roundRectPath(ctx, x0, y, ww * Math.min(1, wv.f), 4 * s, 2 * s);
    ctx.fillStyle = mixHex(UI.gold, elementColor(wv.el), 0.5 + 0.2 * Math.sin(time * 10));
    ctx.fill();
  }

  /** The Grand Magick the queue and stash make: ready (gold), incanting (a filling bar) or cooling down. */
  private drawGrand(ctx: Ctx, me: LocalView, w: number, h: number, s: number, time: number): void {
    const g = me.grand;
    if (!g) return;
    const name = GRAND_MAGICKS[g.id].name;
    const incanting = g.f !== undefined;
    const cooling = !incanting && g.cd > 0;
    const right = incanting ? `${Math.round((g.f ?? 0) * 100)}%` : cooling ? `ready in ${Math.ceil(g.cd)} s` : labels.magick;
    ctx.font = font(17 * s, 900);
    const nameW = ctx.measureText(name.toUpperCase()).width;
    ctx.font = font(13 * s, 800);
    const rightW = ctx.measureText(right).width + 14 * s;
    const pw = 16 * s + nameW + 12 * s + rightW + 14 * s;
    const L = magickPillLayout(w, h, s, pw);
    const glow = cooling ? 0 : 0.5 + 0.5 * Math.sin(time * 5);
    const ph = PILL_H * s;
    roundRectPath(ctx, L.x, L.y, pw, ph, ph / 2);
    ctx.fillStyle = cooling ? UI.panel : mixHex(UI.panelSolid, UI.gold, 0.12 + glow * 0.08);
    ctx.fill();
    if (incanting) {
      ctx.save();
      roundRectPath(ctx, L.x, L.y, pw, ph, ph / 2);
      ctx.clip();
      ctx.fillStyle = rgba(UI.gold, 0.35);
      ctx.fillRect(L.x, L.y, pw * Math.min(1, g.f ?? 0), ph);
      ctx.restore();
    }
    roundRectPath(ctx, L.x, L.y, pw, ph, ph / 2);
    ctx.strokeStyle = cooling ? UI.dim : UI.gold;
    ctx.lineWidth = 2 * s;
    ctx.stroke();
    const cy = L.y + ph / 2;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    ctx.font = font(17 * s, 900);
    ctx.fillStyle = cooling ? UI.muted : UI.gold;
    ctx.fillText(name.toUpperCase(), L.x + 16 * s, cy);
    const rx = L.x + 16 * s + nameW + 12 * s;
    ctx.font = font(13 * s, 800);
    if (!incanting && !cooling) {
      roundRectPath(ctx, rx, cy - 10 * s, rightW, 20 * s, 4 * s);
      ctx.fillStyle = rgba(UI.white, 0.14);
      ctx.fill();
    }
    ctx.textAlign = 'center';
    ctx.fillStyle = cooling ? UI.muted : UI.text;
    ctx.fillText(right, rx + rightW / 2, cy);
  }
}
